'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Minus, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { esDiaSiguiente, horaDeSalida, ordenarHorasDesde } from '@/lib/programa-ss';
import type { ItinerarioPunto } from '@/lib/types';

/**
 * Todas las horas del cuadro caen en la rejilla de 10 minutos: es lo que pidió
 * el usuario y además garantiza que dos puntos nunca compartan clave.
 */
export const PASO_MINUTOS = 10;

export type TipoPunto = ItinerarioPunto['tipo'];
export type ItinerarioRecord = Record<string, ItinerarioPunto>;

// Mismo lenguaje de color que la tabla del libro: Carrera Oficial en rojo y
// Catedral en azul claro.
export const TIPO_PUNTO_STYLE: Record<string, string> = {
  salida: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  itinerario: 'bg-white/5 text-muted-foreground border-white/10',
  carreraOficial: 'bg-red-600/20 text-red-400 border-red-600/40',
  catedral: 'bg-sky-300/20 text-sky-300 border-sky-300/40',
  entrada: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};

export const TIPO_PUNTO_LABEL: Record<string, string> = {
  salida: 'Salida',
  itinerario: 'Itinerario',
  carreraOficial: 'Carrera Oficial',
  catedral: 'Catedral',
  entrada: 'Entrada',
};

/** Tipos disponibles: en vísperas no hay Carrera Oficial ni Catedral. */
function tiposDisponibles(esVispera?: boolean): TipoPunto[] {
  return esVispera
    ? ['salida', 'itinerario', 'entrada']
    : ['salida', 'itinerario', 'carreraOficial', 'catedral', 'entrada'];
}

export function minutosDeHora(hora: string): number {
  const [h, m] = (hora || '00:00').split(':').map((n) => parseInt(n, 10));
  if (isNaN(h) || isNaN(m)) return 0;
  return h * 60 + m;
}

export function horaDeMinutos(minutos: number): string {
  const m = ((Math.round(minutos) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Redondea a la rejilla de 10 minutos. */
function ajustaPaso(minutos: number): number {
  return Math.round(minutos / PASO_MINUTOS) * PASO_MINUTOS;
}

// ─── Modelo interno: una secuencia, no un mapa ───────────────────────────────
// El cuadro se edita como una lista ordenada de puntos donde cada uno guarda
// los minutos que pasan desde el anterior. Así el hermano mayor solo toca la
// hora de salida y los huecos, las horas nunca chocan y mover un punto arrastra
// a todos los siguientes (que es como se hace un cuadro de horarios de verdad).

export interface FilaHorario {
  id: string;
  calle: string;
  tipo: TipoPunto;
  /** Minutos desde el punto anterior. La primera fila lo ignora. */
  gap: number;
}

let contadorFilas = 0;
const nuevaId = () => `f${++contadorFilas}_${Date.now().toString(36)}`;

/** Pasa el mapa guardado en Firestore a la secuencia que edita el componente. */
export function itinerarioAFilas(itinerario: ItinerarioRecord | undefined): { inicio: string; filas: FilaHorario[] } {
  // El orden arranca en la salida y va cruzando la medianoche: un cortejo que se
  // recoge a las 02:30 va DESPUÉS del punto de las 23:50, no al principio.
  const horas = ordenarHorasDesde(Object.keys(itinerario || {}), horaDeSalida(itinerario));
  if (horas.length === 0) return { inicio: '18:00', filas: [] };

  const inicio = horaDeMinutos(ajustaPaso(minutosDeHora(horas[0])));
  let anterior = minutosDeHora(horas[0]);
  const filas: FilaHorario[] = horas.map((hora, i) => {
    const actual = minutosDeHora(hora);
    // Si la hora "retrocede" es que el cortejo ha pasado la medianoche.
    let gap = i === 0 ? 0 : ajustaPaso(actual - anterior + (actual <= anterior ? 1440 : 0));
    if (i > 0 && gap < PASO_MINUTOS) gap = PASO_MINUTOS;
    anterior = actual;
    return { id: nuevaId(), calle: itinerario![hora].calle || '', tipo: (itinerario![hora].tipo || 'itinerario') as TipoPunto, gap };
  });
  return { inicio, filas };
}

/** Calcula la hora de cada fila acumulando los huecos desde la salida. */
export function horasDeFilas(inicio: string, filas: FilaHorario[]): string[] {
  let acumulado = minutosDeHora(inicio);
  return filas.map((fila, i) => {
    if (i > 0) acumulado += fila.gap;
    return horaDeMinutos(acumulado);
  });
}

export function filasAItinerario(inicio: string, filas: FilaHorario[]): ItinerarioRecord {
  const horas = horasDeFilas(inicio, filas);
  const record: ItinerarioRecord = {};
  filas.forEach((fila, i) => {
    if (!fila.calle.trim()) return;
    record[horas[i]] = { calle: fila.calle.trim(), tipo: fila.tipo };
  });
  return record;
}

export interface EstadoCuadro {
  valido: boolean;
  error: string | null;
  /** Número de puntos con calle escrita. */
  puntos: number;
}

function validaCuadro(inicio: string, filas: FilaHorario[]): EstadoCuadro {
  const conCalle = filas.filter((f) => f.calle.trim());
  if (conCalle.length < 2) {
    return { valido: false, error: 'Añade al menos dos puntos con su calle o lugar.', puntos: conCalle.length };
  }
  if (filas.some((f) => !f.calle.trim())) {
    return { valido: false, error: 'Hay puntos sin calle: escríbelos o bórralos.', puntos: conCalle.length };
  }
  const duracion = filas.slice(1).reduce((n, f) => n + f.gap, 0);
  if (duracion >= 1440 - PASO_MINUTOS) {
    return { valido: false, error: 'El cuadro no puede durar más de 23 h 50 min.', puntos: conCalle.length };
  }
  if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(inicio)) {
    return { valido: false, error: 'La hora de salida no es válida.', puntos: conCalle.length };
  }
  return { valido: true, error: null, puntos: conCalle.length };
}

// ─── Editor ──────────────────────────────────────────────────────────────────

export function CuadroHorariosEditor({
  value,
  onChange,
  esVispera,
  className,
}: {
  value: ItinerarioRecord | undefined;
  onChange: (itinerario: ItinerarioRecord, estado: EstadoCuadro) => void;
  esVispera?: boolean;
  className?: string;
}) {
  // El valor entrante solo siembra el estado inicial; a partir de ahí manda el
  // editor. Para recargarlo desde fuera basta con montar con otra `key`.
  const semilla = useRef(itinerarioAFilas(value)).current;
  const [inicio, setInicio] = useState(semilla.inicio);
  const [filas, setFilas] = useState<FilaHorario[]>(semilla.filas);

  const horas = useMemo(() => horasDeFilas(inicio, filas), [inicio, filas]);
  const estado = useMemo(() => validaCuadro(inicio, filas), [inicio, filas]);
  const tipos = tiposDisponibles(esVispera);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current(filasAItinerario(inicio, filas), estado);
  }, [inicio, filas, estado]);

  const duracion = filas.slice(1).reduce((n, f) => n + f.gap, 0);

  const moverInicio = (delta: number) => setInicio((h) => horaDeMinutos(ajustaPaso(minutosDeHora(h)) + delta));

  const actualizar = (id: string, cambios: Partial<FilaHorario>) =>
    setFilas((prev) => prev.map((f) => (f.id === id ? { ...f, ...cambios } : f)));

  const borrar = (id: string) => setFilas((prev) => prev.filter((f) => f.id !== id));

  const mover = (index: number, delta: number) => {
    setFilas((prev) => {
      const destino = index + delta;
      if (destino < 0 || destino >= prev.length) return prev;
      const copia = [...prev];
      [copia[index], copia[destino]] = [copia[destino], copia[index]];
      return copia;
    });
  };

  const anadir = (tipo: TipoPunto = 'itinerario') =>
    setFilas((prev) => [
      ...prev,
      { id: nuevaId(), calle: '', tipo: prev.length === 0 ? 'salida' : tipo, gap: PASO_MINUTOS },
    ]);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Salida y resumen */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-white/8 bg-muted/40 p-3">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs"><Clock className="h-3.5 w-3.5 text-primary" /> Hora de salida</Label>
          <div className="flex items-center gap-1">
            <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => moverInicio(-PASO_MINUTOS)}>
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Input
              type="time"
              step={PASO_MINUTOS * 60}
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
              onBlur={(e) => setInicio(horaDeMinutos(ajustaPaso(minutosDeHora(e.target.value))))}
              className="w-28 text-center font-mono font-bold"
            />
            <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => moverInicio(PASO_MINUTOS)}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 pb-1">
          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[0.7rem] font-semibold">
            {estado.puntos} punto{estado.puntos === 1 ? '' : 's'}
          </span>
          {filas.length > 1 && (
            <>
              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[0.7rem] font-semibold">
                {Math.floor(duracion / 60)} h {duracion % 60} min
              </span>
              <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[0.7rem] font-semibold text-sky-300">
                Recogida {horas[horas.length - 1]}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Puntos */}
      <div className="space-y-1.5">
        {filas.map((fila, index) => (
          <div key={fila.id} className="rounded-xl border border-white/8 bg-background/60 p-2">
            <div className="flex flex-wrap items-center gap-2">
              {/* Si el cortejo cruza la medianoche, esa hora ya es del día
                  siguiente y se avisa aquí mismo (el orden ya lo refleja). */}
              <span className="relative w-14 flex-shrink-0 rounded-lg bg-primary/15 py-1 text-center font-mono text-sm font-bold text-primary">
                {horas[index]}
                {esDiaSiguiente(horas[index], inicio) && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-sky-500 px-1 text-[0.55rem] font-black text-white" title="Ya es el día siguiente">
                    +1
                  </span>
                )}
              </span>

              {index === 0 ? (
                <span className="w-24 flex-shrink-0 text-center text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                  Salida
                </span>
              ) : (
                <div className="flex w-24 flex-shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => actualizar(fila.id, { gap: Math.max(PASO_MINUTOS, fila.gap - PASO_MINUTOS) })}
                    disabled={fila.gap <= PASO_MINUTOS}
                    title="Menos tiempo desde el punto anterior"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-10 text-center font-mono text-xs text-muted-foreground">+{fila.gap}′</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => actualizar(fila.id, { gap: fila.gap + PASO_MINUTOS })}
                    title="Más tiempo desde el punto anterior"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <Input
                value={fila.calle}
                onChange={(e) => actualizar(fila.id, { calle: e.target.value })}
                placeholder="Calle o lugar…"
                className="h-9 min-w-[9rem] flex-1"
              />

              <Select value={fila.tipo} onValueChange={(v) => actualizar(fila.id, { tipo: v as TipoPunto })}>
                <SelectTrigger className="h-9 w-36 flex-shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => <SelectItem key={t} value={t}>{TIPO_PUNTO_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="flex flex-shrink-0 items-center">
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => mover(index, -1)} disabled={index === 0} title="Subir">
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => mover(index, 1)} disabled={index === filas.length - 1} title="Bajar">
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => borrar(fila.id)} title="Borrar punto">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {filas.length === 0 && (
          <p className="rounded-xl border-2 border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground">
            Todavía no hay ningún punto. Empieza por la salida de tu iglesia.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => anadir('itinerario')}>
          <Plus className="h-3.5 w-3.5" /> Añadir punto
        </Button>
        {!esVispera && filas.length > 0 && (
          <>
            <Button type="button" size="sm" variant="ghost" className="text-red-400" onClick={() => anadir('carreraOficial')}>
              <Plus className="h-3 w-3" /> Carrera Oficial
            </Button>
            <Button type="button" size="sm" variant="ghost" className="text-sky-300" onClick={() => anadir('catedral')}>
              <Plus className="h-3 w-3" /> Catedral
            </Button>
          </>
        )}
        {filas.length > 0 && (
          <Button type="button" size="sm" variant="ghost" className="text-violet-300" onClick={() => anadir('entrada')}>
            <Plus className="h-3 w-3" /> Entrada
          </Button>
        )}
        <p className="ml-auto text-[0.65rem] text-muted-foreground">
          Las horas van de {PASO_MINUTOS} en {PASO_MINUTOS} minutos. Cambia un hueco y se recolocan las siguientes.
        </p>
      </div>

      {estado.error && <p className="text-xs text-amber-400">{estado.error}</p>}
    </div>
  );
}

// ─── Vista de solo lectura ───────────────────────────────────────────────────

export function CuadroHorariosTabla({ itinerario, className }: { itinerario: ItinerarioRecord | undefined; className?: string }) {
  const salida = useMemo(() => horaDeSalida(itinerario), [itinerario]);
  const horas = useMemo(() => ordenarHorasDesde(Object.keys(itinerario || {}), salida), [itinerario, salida]);

  if (horas.length === 0) {
    return <p className="py-4 text-center text-xs text-muted-foreground">Sin cuadro de horarios todavía.</p>;
  }

  return (
    <div className={cn('space-y-1', className)}>
      {horas.map((hora) => (
        <div key={hora} className="flex items-center gap-2 rounded-lg border border-white/8 bg-muted/40 p-2 text-sm">
          <span className="relative w-14 flex-shrink-0 rounded bg-primary/15 py-0.5 text-center font-mono text-xs font-bold text-primary">
            {hora}
            {esDiaSiguiente(hora, salida) && (
              <span className="absolute -right-1 -top-1 rounded-full bg-sky-500 px-1 text-[0.5rem] font-black text-white" title="Ya es el día siguiente">+1</span>
            )}
          </span>
          <span className="min-w-0 flex-1 truncate">{itinerario![hora].calle}</span>
          <span className={cn(
            'flex-shrink-0 rounded-full border px-1.5 py-px text-[0.6rem] font-semibold',
            TIPO_PUNTO_STYLE[itinerario![hora].tipo] || TIPO_PUNTO_STYLE.itinerario,
          )}>
            {TIPO_PUNTO_LABEL[itinerario![hora].tipo] || 'Itinerario'}
          </span>
        </div>
      ))}
    </div>
  );
}
